import { z } from 'zod';
import {
  recurrenceDeleteHandlings,
  recurrenceEndModes,
  recurrenceFrequencies,
  recurrenceScopes,
  type RecurrenceEndMode,
  type RecurrenceFrequency,
} from '../enums.js';
import { optionalText } from './_fields.js';
import {
  appointmentCreateStatuses,
  type AppointmentCheckAbsenceDto,
  type AppointmentDto,
  type AppointmentOverlapDto,
} from './agenda-appointments.js';
import { dateOnlySchema } from './patients.js';

/**
 * Rendez-vous récurrents. Une série porte la règle ; **chaque occurrence est un vrai
 * rendez-vous**, avec son identifiant, son statut, sa facture éventuelle. Le front n'a donc rien
 * de spécial à faire pour afficher une occurrence : c'est un `AppointmentDto` qui porte en plus
 * `seriesId` et `occurrenceIndex`.
 */

/** Rang de la première occurrence. Il est montré à l'humain — « séance 3 sur 10 » — donc il part de 1. */
export const FIRST_OCCURRENCE_INDEX = 1;

/** Plafond du nombre de séances demandables à la création, repris de la V1. */
export const MAX_REQUESTED_OCCURRENCES = 365;

/**
 * Jusqu'où une série est écrite en base d'avance. Une série sans fin ne peut pas produire une
 * infinité de lignes : elle est matérialisée par tranches, et une tâche planifiée repousse
 * l'horizon. Dix-huit mois laissent largement de quoi remplir un agenda visible, et bornent le
 * dégât d'une règle mal saisie.
 */
export const RECURRENCE_HORIZON_MONTHS = 18;

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * La règle, sans le gabarit. `daysOfWeek` ne vaut que pour l'hebdomadaire et le bimensuel ;
 * absent, c'est le jour de semaine de la première date qui fait foi.
 */
export const recurrenceRuleSchema = z
  .object({
    frequency: z.enum(recurrenceFrequencies),
    interval: z.int().min(1).max(99),
    /** Norme JavaScript : 0 = dimanche, 6 = samedi. */
    daysOfWeek: z
      .array(z.int().min(0).max(6))
      .min(1)
      .max(7)
      .refine(
        (days) => new Set(days).size === days.length,
        'Un jour ne peut apparaître qu’une fois',
      )
      .optional(),
    endMode: z.enum(recurrenceEndModes),
    /** Dernière date incluse. Exigé par `on_date`, interdit ailleurs. */
    endDate: dateOnlySchema.optional(),
    /** Nombre de séances visé. Exigé par `after`, interdit ailleurs. */
    maxOccurrences: z.int().min(1).max(MAX_REQUESTED_OCCURRENCES).optional(),
  })
  .strict()
  .refine((r) => (r.endMode === 'on_date') === (r.endDate !== undefined), {
    error: 'Une fin à une date exige cette date, et elle seule',
    path: ['endDate'],
  })
  .refine((r) => (r.endMode === 'after') === (r.maxOccurrences !== undefined), {
    error: 'Une fin après N séances exige ce nombre, et lui seul',
    path: ['maxOccurrences'],
  })
  .refine((r) => r.frequency === 'weekly' || r.frequency === 'biweekly' || !r.daysOfWeek, {
    error: 'Les jours de semaine ne s’appliquent qu’à une cadence hebdomadaire ou bimensuelle',
    path: ['daysOfWeek'],
  });
export type RecurrenceRuleInput = z.infer<typeof recurrenceRuleSchema>;

/**
 * Création d'une série. Le gabarit est celui d'un rendez-vous ordinaire, mot pour mot : c'est le
 * même dialogue côté front, avec un panneau de récurrence en plus. `startsAt` donne à la fois la
 * date d'ancrage et l'heure murale que toutes les occurrences reprendront.
 */
export const appointmentSeriesCreateSchema = z
  .object({
    patientId: z.uuid().nullable().optional(),
    practitionerMemberId: z.uuid(),
    locationId: z.uuid().nullable().optional(),
    consultationTypeId: z.uuid().nullable().optional(),
    startsAt: z.iso.datetime(),
    /** Absente : la durée du type de consultation, sinon la durée par défaut du produit. */
    durationMinutes: z.int().min(5).max(600).optional(),
    status: z.enum(appointmentCreateStatuses).optional(),
    notes: optionalText(4000).optional(),
    privateNotes: optionalText(4000).optional(),
    /**
     * Une seule occurrence en conflit annule toute la création : le serveur répond 409 en listant
     * lesquelles. Il n'y a plus de dérogation depuis le 30 septembre 2026 — deux rendez-vous d'un
     * même praticien, ou de tout le cabinet en offre à un praticien, ne se chevauchent jamais.
     */
    recurrence: recurrenceRuleSchema,
  })
  .strict();
export type AppointmentSeriesCreateInput = z.infer<typeof appointmentSeriesCreateSchema>;

/** Même corps que la création : la prévisualisation est un essai à blanc, pas un autre contrat. */
export const appointmentSeriesPreviewSchema = appointmentSeriesCreateSchema;

export const appointmentSeriesIdParamSchema = z.object({ id: z.uuid() }).strict();

/**
 * Portée d'une suppression d'occurrence, en paramètres d'URL : un `DELETE` ne porte pas de corps.
 * `occurrenceHandling` ne vaut que pour `this` sur une série « après N séances ».
 */
export const appointmentDeleteQuerySchema = z
  .object({
    scope: z.enum(recurrenceScopes).default('this'),
    occurrenceHandling: z.enum(recurrenceDeleteHandlings).default('count'),
  })
  .strict();
export type AppointmentDeleteQuery = z.infer<typeof appointmentDeleteQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export interface AppointmentSeriesDto {
  id: string;
  practitionerMemberId: string;
  patientId: string | null;
  locationId: string | null;
  consultationTypeId: string | null;
  durationMinutes: number;
  notes: string | null;
  privateNotes: string | null;
  frequency: RecurrenceFrequency;
  interval: number;
  daysOfWeek: number[] | null;
  /** Origine du calendrier. La première occurrence peut tomber plus tard, jamais plus tôt. */
  anchorDate: string;
  /** Heure murale `HH:MM` du cabinet, la même à chaque occurrence, changement d'heure compris. */
  startTime: string;
  endMode: RecurrenceEndMode;
  endDate: string | null;
  maxOccurrences: number | null;
  /** Rangs rendus par une suppression « libérer », qui rallongent d'autant le quota. */
  releasedCount: number;
  /** Date jusqu'à laquelle la règle a été dépliée en base. */
  materializedUntil: string;
  /** Faux quand la série n'a plus rien à produire : fin atteinte, scindée, ou arrêtée. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Une date que la règle produirait, avant toute écriture. */
export interface SeriesOccurrencePreviewDto {
  index: number;
  startsAt: string;
  endsAt: string;
}

/** Ce qu'une occurrence heurterait, rang par rang : de quoi afficher les conflits par date. */
export interface SeriesOccurrenceConflictDto {
  index: number;
  startsAt: string;
  endsAt: string;
  overlaps: AppointmentOverlapDto[];
  absences: AppointmentCheckAbsenceDto[];
}

export interface AppointmentSeriesPreviewDto {
  /** Occurrences que la création écrirait maintenant. */
  count: number;
  occurrences: SeriesOccurrencePreviewDto[];
  conflicts: SeriesOccurrenceConflictDto[];
  /** Dernière date dépliée. */
  horizonDate: string;
  /**
   * Vrai quand la règle continue au-delà de l'horizon : la série ne s'arrête pas là, le reste
   * sera écrit par la tâche planifiée au fil du temps.
   */
  continuesBeyondHorizon: boolean;
}

export interface AppointmentSeriesDetailDto {
  series: AppointmentSeriesDto;
  /** Occurrences réellement en base, par rang croissant. */
  occurrences: AppointmentDto[];
}

/** Réponse d'une action à portée : ce qui a bougé, et dans quelle série. */
export interface AppointmentScopeResultDto {
  /** Nombre d'occurrences effectivement touchées. */
  affectedCount: number;
  /** Série d'arrivée : la même, ou la nouvelle née d'un « les suivantes ». */
  seriesId: string | null;
}
